import { ReadonlyRadioGroup } from '@exam-center/components';
import {
  Box,
  Flex,
  Grid,
  SectionTitle,
  Text,
  ReadonlyField,
  WatchLaterOutlinedIcon,
} from '@shared/components';
import { dayjs, formatSecondsToHoursMinutes, randomUUID } from '@shared/utils';

import HighlightCard from './highlight-card';

export interface DurationSettingsReadonlyFieldsProps {
  visibleFields: {
    singleSessionDuration: boolean;
    multipleSessionDuration: boolean;
  };
  values: {
    type?: 'single-session' | 'multiple-session';
    sessions?: { value: number; order: number }[];
  };
}

export default function DurationSettingsReadonlyFields(
  props: Readonly<DurationSettingsReadonlyFieldsProps>,
) {
  const { visibleFields, values } = props;

  const singleSessionValue = dayjs
    .duration(values.sessions?.[0]?.value ?? 0, 'seconds')
    .format('HH:mm');

  const multipleSessionValue =
    values.sessions?.map((session) => {
      return {
        value: dayjs.duration(session.value, 'seconds').format('HH:mm'),
        order: session.order,
      };
    }) ?? [];

  const totalTime = values.sessions?.reduce((acc, session) => acc + session.value, 0) ?? 0;

  return (
    <>
      <SectionTitle title='Exam Duration' />
      <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
        <Box>
          <Text variant='body1Semibold'>ระยะเวลาทำข้อสอบ</Text>
        </Box>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <ReadonlyRadioGroup
            options={[
              {
                label: (
                  <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
                    <Flex
                      sx={{
                        flexDirection: 'column',
                        gap: (theme) => theme.spacing(2),
                      }}
                    >
                      <Text variant='body1'>กำหนดระยะเวลาสอบแบบ 1 ช่วง</Text>
                      <Text variant='caption' color='textSecondary'>
                        ใช้สำหรับหลักสูตรที่มีการสอบเพียงครั้งเดียวโดยไม่แบ่งช่วงเวลา
                      </Text>
                    </Flex>
                    {visibleFields.singleSessionDuration ? (
                      <ReadonlyField
                        label='ระยะเวลาสอบ (HH:MM)'
                        sx={{ mt: 2 }}
                        content={
                          <Flex
                            sx={{
                              gap: (theme) => theme.spacing(1),
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text>{singleSessionValue}</Text>
                            <WatchLaterOutlinedIcon
                              sx={(theme) => ({
                                color: theme.palette.text.secondary,
                              })}
                            />
                          </Flex>
                        }
                      />
                    ) : null}
                  </Flex>
                ),
                checked: values.type === 'single-session',
                sx: { alignItems: 'start' },
              },
              {
                label: (
                  <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
                    <Flex
                      sx={{
                        flexDirection: 'column',
                        gap: (theme) => theme.spacing(2),
                      }}
                    >
                      <Text variant='body1'>กำหนดระยะเวลาสอบแบบหลายช่วง (สูงสุด 4 ช่วง)</Text>
                      <Text variant='caption' color='textSecondary'>
                        เหมาะสำหรับหลักสูตรที่แบ่งการสอบออกเป็นหลายช่วงเวลา
                      </Text>
                    </Flex>
                  </Flex>
                ),
                checked: values.type === 'multiple-session',
                sx: { alignItems: 'start' },
              },
            ]}
          />
          {values.type === 'multiple-session' ? (
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(4),
                ml: theme.spacing(8),
              })}
            >
              {multipleSessionValue?.map((session) => (
                <HighlightCard
                  key={randomUUID()}
                  sx={(theme) => ({
                    gridTemplateColumns: 'max-content minmax(auto, 240px)',
                    borderRadius: theme.shape.rounded(2),
                    width: '100%',
                  })}
                >
                  <Text variant='body1Semibold'> ช่วงที่ {session.order}</Text>
                  <ReadonlyField
                    label='ระยะเวลาสอบ (HH:MM)'
                    content={
                      <Flex
                        sx={{
                          gap: (theme) => theme.spacing(1),
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flex: 1,
                        }}
                      >
                        <Text>{session.value}</Text>
                        <WatchLaterOutlinedIcon
                          sx={(theme) => ({
                            color: theme.palette.text.secondary,
                          })}
                        />
                      </Flex>
                    }
                    sx={{ width: '100%' }}
                  />
                </HighlightCard>
              ))}
              <Flex
                sx={(theme) => ({
                  p: theme.spacing(3, 4),
                  gap: theme.spacing(2),
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: theme.shape.rounded(2),
                  backgroundColor: theme.palette.grey[50],
                })}
              >
                <WatchLaterOutlinedIcon sx={{ fontSize: '24px' }} />
                <Text variant='body1Semibold'>
                  รวมระยะเวลาสอบ: {formatSecondsToHoursMinutes(totalTime, 'TEXT')}
                </Text>
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </Grid>
    </>
  );
}
